import { Link } from 'react-router-dom';
import './OrderTable.css';

function OrderTable({ orders, sortBy, sortOrder, onSort }) {
  const handleSort = (field) => {
    if (sortBy === field) {
      onSort(field, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(field, 'desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="sort-icon">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="sort-icon active">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="sort-icon active">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', className: 'badge-warning' },
      processing: { label: 'Processing', className: 'badge-info' },
      shipped: { label: 'Shipped', className: 'badge-purple' },
      delivered: { label: 'Delivered', className: 'badge-success' },
      cancelled: { label: 'Cancelled', className: 'badge-danger' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`badge ${config.className}`}>{config.label}</span>;
  };

  const getPaymentBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', className: 'badge-warning' },
      paid: { label: 'Paid', className: 'badge-success' },
      failed: { label: 'Failed', className: 'badge-danger' },
      refunded: { label: 'Refunded', className: 'badge-secondary' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`badge ${config.className}`}>{config.label}</span>;
  };

  if (orders.length === 0) {
    return (
      <div className="table-empty">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
        </svg>
        <h3>No orders found</h3>
        <p>Try adjusting your search or filter criteria.</p>
      </div>
    );
  }

  return (
    <div className="table-container">
      <table className="order-table">
        <thead>
          <tr>
            <th className="sortable" onClick={() => handleSort('orderNumber')}>
              Order {getSortIcon('orderNumber')}
            </th>
            <th className="sortable" onClick={() => handleSort('customer')}>
              Customer {getSortIcon('customer')}
            </th>
            <th className="sortable" onClick={() => handleSort('createdAt')}>
              Date {getSortIcon('createdAt')}
            </th>
            <th className="sortable" onClick={() => handleSort('status')}>
              Status {getSortIcon('status')}
            </th>
            <th>Payment</th>
            <th className="sortable text-right" onClick={() => handleSort('total')}>
              Total {getSortIcon('total')}
            </th>
            <th className="text-center">Items</th>
            <th className="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.id}>
              <td>
                <Link to={`/orders/${order.id}`} className="order-link">
                  {order.orderNumber}
                </Link>
              </td>
              <td>
                <div className="customer-info">
                  <span className="customer-name">
                    {order.customer.firstName} {order.customer.lastName}
                  </span>
                  <span className="customer-email">{order.customer.email}</span>
                </div>
              </td>
              <td className="date-cell">{formatDate(order.createdAt)}</td>
              <td>{getStatusBadge(order.status)}</td>
              <td>{getPaymentBadge(order.paymentStatus)}</td>
              <td className="text-right price-cell">{formatPrice(order.total)}</td>
              <td className="text-center items-cell">{order.items.length}</td>
              <td className="text-right">
                <div className="table-actions">
                  <Link to={`/orders/${order.id}`} className="btn-icon" title="View order">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default OrderTable;
